import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faUserMd, 
  faTags, 
  faMicroscope, 
  faClipboardList,
  faUsers,
  faQuestionCircle,
  faNewspaper,
  faBuilding,
  faShieldAlt,
  faEnvelope,
  faSignInAlt,
  faSignOutAlt,
  faTimes,
  faChartBar
} from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import { auth } from '../firebase-config';

const MobileNav = ({ isOpen, onClose, onLoginClick, user, onNavigate }) => {
  const location = useLocation();
  
  const handleClick = (path) => {
    onNavigate(path);
    onClose();
  };

  // Add this useEffect for better handling of body scrolling
  React.useEffect(() => {
    if (isOpen) {
      // Prevent background scrolling when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scrolling when menu is closed
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1999,
          }}
          onClick={onClose}
        />
      )}
      
      {/* Menu */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: '250px',
        height: '100%',
        maxHeight: '100%',
        backgroundColor: 'white',
        zIndex: 2000,
        transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s ease',
        boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '1rem',
          flexShrink: 0
        }}>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: '#2c3e50',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <nav style={{ flexGrow: 1, overflowY: 'auto' }}>
          <ul style={{ 
            listStyle: 'none', 
            padding: 0, 
            margin: 0,
            paddingBottom: '100px' 
          }}>
            <NavItem 
              icon={faHome} 
              label="Home" 
              path="/" 
              currentPath={location.pathname} 
              onClick={() => handleClick('/')}
            />
            <NavItem 
              icon={faUserMd} 
              label="Primary Care Physicians/EMRs" 
              path="/pcp" 
              currentPath={location.pathname} 
              onClick={() => handleClick('/pcp')}
            />
            <NavItem 
              icon={faTags} 
              label="Pricing" 
              path="/pricing" 
              currentPath={location.pathname} 
              onClick={() => handleClick('/pricing')}
            />
            <NavItem 
              icon={faMicroscope} 
              label="Clinical Study" 
              path="/clinical-study" 
              currentPath={location.pathname} 
              onClick={() => handleClick('/clinical-study')}
            />
            <NavItem 
              icon={faChartBar} 
              label="Example Report" 
              path="/example-report" 
              currentPath={location.pathname} 
              onClick={() => handleClick('/example-report')}
            />
            <NavItem 
              icon={faClipboardList} 
              label="Questionnaire" 
              path="/questionnaire" 
              currentPath={location.pathname} 
              onClick={() => {
                // Check if mobile
                const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                
                // Create URL with a param to signal it's a new window
                const url = isMobile 
                  ? '/questionnaire?new=true&mobile=true&t=' + Date.now() 
                  : '/questionnaire';
                  
                // Open in new tab
                const newWindow = window.open(url, '_blank');
                
                // For mobile, try more aggressively to control scrolling
                if (newWindow && isMobile) {
                  try {
                    newWindow.addEventListener('load', () => {
                      // Use multiple scroll attempts with delays
                      [0, 100, 300, 500, 1000].forEach(delay => {
                        setTimeout(() => {
                          newWindow.scrollTo(0, 0);
                        }, delay);
                      });
                    });
                  } catch (e) {
                    console.log('Could not access new window', e);
                  }
                }
              }}
            />
            <NavItem 
              icon={faClipboardList} 
              label="Epitome" 
              path="/epitome" 
              currentPath={location.pathname} 
              onClick={() => {
                // Check if mobile
                const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                
                // Create URL with a param to signal it's a new window
                const url = isMobile 
                  ? '/epitome?new=true&mobile=true&t=' + Date.now() 
                  : '/epitome';
                  
                // Open in new tab
                const newWindow = window.open(url, '_blank');
                
                // For mobile, try more aggressively to control scrolling
                if (newWindow && isMobile) {
                  try {
                    newWindow.addEventListener('load', () => {
                      // Use multiple scroll attempts with delays
                      [0, 100, 300, 500, 1000].forEach(delay => {
                        setTimeout(() => {
                          newWindow.scrollTo(0, 0);
                        }, delay);
                      });
                    });
                  } catch (e) {
                    console.log('Could not access new window', e);
                  }
                }
              }}
            />
            <NavItem 
              icon={faUsers} 
              label="People" 
              path="/people" 
              currentPath={location.pathname} 
              onClick={() => handleClick('/people')}
            />
            <NavItem 
              icon={faQuestionCircle} 
              label="FAQ" 
              path="/faq" 
              currentPath={location.pathname} 
              onClick={() => handleClick('/faq')}
            />
            <NavItem 
              icon={faNewspaper} 
              label="News" 
              path="/news" 
              currentPath={location.pathname} 
              onClick={() => handleClick('/news')}
            />
            <NavItem 
              icon={faBuilding} 
              label="Corporate" 
              path="/corporate" 
              currentPath={location.pathname} 
              onClick={() => handleClick('/corporate')}
            />
            <NavItem 
              icon={faShieldAlt} 
              label="Privacy and Data Collection" 
              path="/privacy" 
              currentPath={location.pathname} 
              onClick={() => handleClick('/privacy')}
            />
            <NavItem 
              icon={faEnvelope} 
              label="Contact" 
              path="/contact" 
              currentPath={location.pathname} 
              onClick={() => handleClick('/contact')}
            />
            {!user ? (
              <NavItem 
                icon={faSignInAlt} 
                label="Login" 
                isLastItem={true}
                onClick={() => {
                  onLoginClick();
                  onClose();
                }}
              />
            ) : (
              <NavItem 
                icon={faSignOutAlt} 
                label="Logout" 
                isLastItem={true}
                onClick={() => {
                  auth.signOut();
                  onClose();
                }}
              />
            )}
            {/* Add an empty spacer element for better scrolling */}
            <li style={{ height: '60px' }}></li>
          </ul>
        </nav>
      </div>
    </>
  );
};

const NavItem = ({ icon, label, path, currentPath, onClick, isLastItem }) => {
  const isActive = path && path === currentPath;
  
  return (
    <li style={{ 
      marginBottom: isLastItem ? '20px' : '2px',
      paddingBottom: isLastItem ? '10px' : '0'
    }}>
      <button
        onClick={onClick}
        style={{
          width: '100%',
          padding: '0.8rem 1.5rem',
          border: 'none',
          background: isActive ? '#e3f2fd' : 'none',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          color: isActive ? '#3498db' : '#2c3e50',
          fontSize: '1rem',
          transition: 'all 0.3s ease',
          position: 'relative',
          fontWeight: isActive ? '600' : 'normal',
          minHeight: '48px'
        }}
      >
        <FontAwesomeIcon 
          icon={icon} 
          style={{ 
            marginRight: '12px',
            width: '20px',
            height: '20px'
          }} 
        />
        <span>{label}</span>
      </button>
    </li>
  );
};

export default MobileNav;